import type { ScheduledReportEmail } from '../../../../lib/api';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { AlertCircle, CalendarClock, Loader2, RefreshCw, Users } from 'lucide-react';

const FORMAT_LABELS: Record<string, string> = {
  excel: 'Excel', xlsx: 'Excel', pdf: 'PDF', png: 'PNG', csv: 'CSV',
};

interface ScheduledReportEmailListProps {
  reports: ScheduledReportEmail[] | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selectedId: number | null;
  onSelect: (report: ScheduledReportEmail) => void;
  /** Helix staff see every company's schedules, so each row names its company. */
  showCompany: boolean;
}

/**
 * The Reports schedules whose emails the viewer may preview: dashboard,
 * cadence, formats, recipient count and when it last ran.
 */
export default function ScheduledReportEmailList({
  reports, loading, error, onRetry, selectedId, onSelect, showCompany,
}: ScheduledReportEmailListProps) {
  const { formatDateTime } = useFormatters();
  const rows = reports ?? [];

  return (
    <Card className="w-[330px] shrink-0 overflow-hidden self-start">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">
          Scheduled report emails{rows.length > 0 ? ` (${rows.length})` : ''}
        </CardTitle>
        <p className="text-xs text-muted-foreground">Set up from a dashboard in Reports, under Schedule.</p>
      </CardHeader>
      <CardContent className="p-0">
        {loading && (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading scheduled reports">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-10 px-4 text-sm">
            <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
            <p className="font-medium">Could not load scheduled reports</p>
            <p className="text-xs text-muted-foreground mt-1">{error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              <RefreshCw className="h-4 w-4 mr-1" /> Try again
            </Button>
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="text-center py-12 px-4">
            <CalendarClock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm font-medium text-muted-foreground">No scheduled reports</p>
            <p className="text-xs text-muted-foreground mt-1">
              Nobody in your company has scheduled a report email yet.
            </p>
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div className="overflow-y-auto max-h-[calc(100vh-22rem)]">
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onSelect(r)}
                className={`w-full text-left px-4 py-3 text-sm transition-colors border-b border-border/50 ${
                  selectedId === r.id ? 'bg-primary/10' : 'hover:bg-accent'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`flex-1 min-w-0 truncate ${selectedId === r.id ? 'text-primary font-medium' : 'font-medium'}`}>
                    {r.dashboard_name}
                  </span>
                  {!r.is_active && <Badge variant="outline" className="text-[10px] font-normal">Paused</Badge>}
                </div>
                {showCompany && <p className="text-xs text-muted-foreground truncate">{r.company_name}</p>}
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <Badge variant="secondary" className="text-[10px]">{r.frequency_display}</Badge>
                  {r.formats.map((f) => (
                    <Badge key={f} variant="outline" className="text-[10px] font-normal">
                      {FORMAT_LABELS[f.toLowerCase()] ?? f.toUpperCase()}
                    </Badge>
                  ))}
                  <span
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground ml-auto"
                    title={`${r.recipient_count} recipient${r.recipient_count === 1 ? '' : 's'}`}
                  >
                    <Users className="h-3 w-3" /> {r.recipient_count}
                  </span>
                </div>
                <p
                  className="text-xs text-muted-foreground mt-1"
                  title="When the schedule last ran. Delivery also depends on the notification email switches."
                >
                  Last run: {r.last_run_at ? formatDateTime(r.last_run_at) : 'Not yet run'}
                </p>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
